import { HttpRequest } from '@angular/common/http';
import { InjectionToken, Type } from '@angular/core';
import { ObjectTools } from '../tools/object-tools';

export interface SolHttpMockDefinition {
  method?: string;
  matcher: RegExp;
  name?: string;
}

export type SolHttpMockErrorBody = Record<string, string[]> | Record<string, any>;

export class SolHttpMockResult<T = unknown> {
  private constructor(
    public readonly status: number | undefined,
    public readonly headers: Record<string, string>,
    public readonly body: T | undefined,
    public readonly error: any | undefined,
    public readonly statusText: string | undefined,
    public readonly url: string | undefined,
    public readonly delayMs: number | undefined,
  ) {}

  public static of<T>(body: T, status: number = 200): SolHttpMockResult<T> {
    return new SolHttpMockResult<T>(status, {}, body, undefined, undefined, undefined, undefined);
  }

  public static ofEmpty<T>(status: number = 200): SolHttpMockResult<T> {
    return new SolHttpMockResult<T>(status, {}, undefined, undefined, undefined, undefined, undefined);
  }

  public static ofError<T>(status: number, body: SolHttpMockErrorBody): SolHttpMockResult<T> {
    return new SolHttpMockResult<T>(status, {}, undefined, body, undefined, undefined, undefined);
  }

  public static ofRaisedError(message: string): never {
    throw new Error(message);
  }

  public withHeaders(headers: Record<string, string>): SolHttpMockResult<T> {
    return this.copy({ headers: { ...headers } });
  }

  public withStatusText(statusText: string): SolHttpMockResult<T> {
    return this.copy({ statusText });
  }

  public withUrl(url: string): SolHttpMockResult<T> {
    return this.copy({ url });
  }

  public withDelayMs(delayMs: number): SolHttpMockResult<T> {
    return this.copy({ delayMs });
  }

  private copy(partial: Partial<SolHttpMockResult<T>>): SolHttpMockResult<T> {
    const next = ObjectTools.copy<SolHttpMockResult<T>>(this, partial);
    return new SolHttpMockResult<T>(
      next.status,
      next.headers,
      next.body,
      next.error,
      next.statusText,
      next.url,
      next.delayMs,
    );
  }
}

export abstract class AbstractSolHttpMock<T> implements SolHttpMockDefinition {
  public readonly method?: string;
  public readonly matcher: RegExp;
  public readonly name?: string;

  protected constructor(definition: SolHttpMockDefinition) {
    this.method = definition.method?.toUpperCase();
    this.matcher = definition.matcher;
    this.name = definition.name;
  }

  public matches(request: HttpRequest<unknown>): boolean {
    const methodMatches = this.method === undefined || request.method === this.method;
    const urlMatches = this.matcher.test(request.url);
    return methodMatches && urlMatches;
  }

  public abstract buildResponse(request: HttpRequest<unknown>): SolHttpMockResult<T> | Promise<SolHttpMockResult<T>>;
}

/**
 * Transform mock: instead of replacing a response, it lets the real request through and rewrites the
 * response body. Register alongside replacement mocks; the interceptor passes the request to
 * `next.handle` and maps the emitted `HttpResponse` body through `transform`. Replacement mocks always
 * take precedence over transform mocks for the same request.
 */
export abstract class AbstractSolHttpResponseTransformMock extends AbstractSolHttpMock<unknown> {
  public buildResponse(): never {
    throw new Error('Transform mocks do not build a replacement response.');
  }

  public abstract transform(body: unknown, request: HttpRequest<unknown>): unknown;
}

export type SolHttpMockConstructor = Type<AbstractSolHttpMock<unknown>>;
export type SolHttpMocks = Array<SolHttpMockConstructor | AbstractSolHttpMock<unknown>>;

export const SOL_HTTP_MOCKS = new InjectionToken<SolHttpMocks>('SOL_HTTP_MOCKS');
